import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import http from "@shared/api/http";
import i18n from "@shared/i18n/i18n";
import { deviceTimezone } from "./pushService";

/**
 * Push for the store app. Its WebView has no Push API, so the native plugin
 * registers the device with Firebase Cloud Messaging instead, and the backend
 * keeps the FCM token in the same table as the browsers' subscriptions — the
 * token is the device's endpoint, platform tells the two apart. Reminder
 * hour, quiet rule and clean-up on account deletion are shared with web push
 * (sophy_web: PushSubscription).
 *
 * Live updates bring this code to store builds that cannot do push yet, so
 * everything checks isNativePushAvailable() first — after nativePushReady,
 * which reads the native app version.
 */

const API = "/:locale/v2/:token/push_subscription.json";
const TOKEN_KEY = "nativePush.token";
const REGISTER_TIMEOUT_MS = 20000;
export const REMINDER_CHANNEL = "reminders";

// The first store version per platform whose native project hands out FCM
// tokens. iOS 2.1.1 ships the plugin but not Firebase Messaging; asking it
// for a token would only fail (docs/mobile-release.md §8).
const MIN_NATIVE_VERSION = { android: "2.1.0", ios: "2.1.2" };

let nativeVersion = null;
/** Reads the native app version; await it before the synchronous checks below. */
export async function nativePushReady() {
  try {
    nativeVersion = Capacitor.isNativePlatform() ? (await App.getInfo()).version : null;
  } catch (e) {
    nativeVersion = null;
  }
}

const olderThan = (version, minimum) => {
  const left = String(version).split(".").map((part) => parseInt(part, 10) || 0);
  const right = String(minimum).split(".").map((part) => parseInt(part, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    if ((left[i] || 0) !== (right[i] || 0)) return (left[i] || 0) < (right[i] || 0);
  }
  return false;
};

// "ready", "update" (a store update brings push), "soon" (no store version
// with push yet) or "none" (browser).
function pushState() {
  try {
    if (!Capacitor.isNativePlatform()) return "none";
    const minimum = MIN_NATIVE_VERSION[Capacitor.getPlatform()];
    if (!minimum) return "none";
    if (!Capacitor.isPluginAvailable("PushNotifications")) return "update";
    if (!nativeVersion || olderThan(nativeVersion, minimum)) return "soon";
    return "ready";
  } catch (e) {
    return "none";
  }
}

export function isNativePushAvailable() {
  return pushState() === "ready";
}

/** A store build that cannot do push, though a newer one from the store can: worth an update. */
export function needsAppUpdateForPush() {
  return pushState() === "update";
}

/** The FCM token this device registered with the backend, if any. */
export function registeredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

/** "granted", "denied" or "default" (not asked yet, or may ask again), like Notification.permission. */
export async function nativePermission() {
  await nativePushReady();
  if (!isNativePushAvailable()) return "unsupported";
  const { receive } = await PushNotifications.checkPermissions();
  if (receive === "granted") return "granted";
  return receive === "denied" ? "denied" : "default";
}

// Android groups notifications by channel; the name shows in the system
// settings. iOS has no channels (the plugin rejects the call there).
async function createReminderChannel() {
  if (Capacitor.getPlatform() !== "android") return;
  await PushNotifications.createChannel({
    id: REMINDER_CHANNEL,
    name: i18n.t("push.channel_name"),
    description: i18n.t("push.channel_description"),
    importance: 3,
  });
}

/** Resolves with the FCM token once the plugin has registered the device. */
async function requestToken() {
  let settle;
  const token = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });
  const failed = (message) => Object.assign(new Error(message || "registration failed"), { code: "error" });
  const handles = await Promise.all([
    PushNotifications.addListener("registration", ({ value }) => settle.resolve(value)),
    PushNotifications.addListener("registrationError", ({ error }) => settle.reject(failed(error))),
  ]);
  const timer = setTimeout(() => settle.reject(failed("registration timed out")), REGISTER_TIMEOUT_MS);
  try {
    await PushNotifications.register();
    return await token;
  } finally {
    clearTimeout(timer);
    handles.forEach((handle) => handle.remove());
  }
}

// Registering again from a device whose token changed moves its row (and the
// patient's settings) to the new token instead of leaving a dead one behind.
async function saveToken(token, attributes = {}) {
  const previous = registeredToken();
  const response = await http.post(API, {
    push_subscription: {
      endpoint: token,
      platform: Capacitor.getPlatform(),
      user_agent: navigator.userAgent,
      timezone: deviceTimezone(),
      ...attributes,
    },
    ...(previous && previous !== token ? { previous_endpoint: previous } : {}),
  });
  localStorage.setItem(TOKEN_KEY, token);
  return response.data;
}

/**
 * Asks for permission (iOS and Android 13+ show a dialog; must run from a tap),
 * registers with FCM and saves the device with the backend. Rejects with
 * { code: "denied" } when the patient declines.
 */
export async function enableNativePush(settings = { reminderHour: 18 }) {
  await nativePushReady();
  if (!isNativePushAvailable()) throw Object.assign(new Error("unsupported"), { code: "unsupported" });

  let { receive } = await PushNotifications.checkPermissions();
  if (receive !== "granted") ({ receive } = await PushNotifications.requestPermissions());
  if (receive !== "granted") throw Object.assign(new Error("denied"), { code: "denied" });

  await createReminderChannel();
  const token = await requestToken();
  return saveToken(token, {
    reminder_hour: settings.reminderHour,
    reminders_enabled: settings.enabled !== false,
  });
}

/** Removes this device from the backend; true when there was one. */
export async function disableNativePush() {
  const token = registeredToken();
  if (!token) return false;
  await http.delete(API, { data: { endpoint: token } });
  localStorage.removeItem(TOKEN_KEY);
  return true;
}

/**
 * On app start with a patient signed in: FCM may have rotated the token, and
 * the patient may have switched notifications off in the system settings
 * since. Quiet on failure — the next start tries again.
 */
export async function syncNativePush() {
  await nativePushReady();
  if (!isNativePushAvailable() || !registeredToken()) return;
  try {
    if ((await nativePermission()) !== "granted") {
      await disableNativePush();
      return;
    }
    await createReminderChannel();
    const token = await requestToken();
    if (token !== registeredToken()) await saveToken(token);
  } catch (error) {
    console.warn("Native push sync failed:", error);
  }
}

/** Calls back with the path a tapped notification points to; returns the unsubscribe. */
export function onNotificationOpened(callback) {
  // Right away, without waiting for the version: a tap that started the app
  // is held by the plugin only until the first listener takes it.
  if (pushState() === "none" || !Capacitor.isPluginAvailable("PushNotifications")) return () => {};
  const handle = PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const url = action?.notification?.data?.url;
    if (typeof url === "string" && url.startsWith("/")) callback(url);
  });
  return () => {
    handle.then((listener) => listener.remove());
  };
}
